import {
  Component, OnInit, OnDestroy, OnChanges, AfterViewInit,
  Input, Output, SimpleChanges, EventEmitter, ViewChild, ElementRef, TemplateRef,
  ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Category } from '../../model/category.model';
import { Product } from '../pages/dashboard/product.store';
import { GeneratorService } from 'src/app/deebr/service/generator.service';
import { SharedDashboardProductService } from 'src/app/deebr/service/shared-dashboard-product.service';
import { SharedProductService } from 'src/app/deebr/service/shared-product.service';
import { ScrappedImagesService } from 'src/app/deebr/service/scrapped-images.service';
import { NbDialogService } from '@nebular/theme';
import { createSwapy } from 'swapy';
import { environment } from 'src/environments/environment';

export interface ProductDisposition {
  /**
   * Identité d'une OCCUPATION d'emplacement (pas du produit, pas de la case).
   * Frappée par TableComponent à chaque placement : deux exemplaires du même
   * produit ont deux instance_id distincts, ce qui permet à chacun d'avoir sa
   * propre image, son propre index de carrousel et sa propre suppression.
   * Sert aussi de `data-swapy-item`, qui doit être unique dans le conteneur.
   */
  instance_id: string;
  col: number;
  row: number;
  colspan: number;
  rowspan: number;
  ordre_generation: number;
  product_id: string | null;
  image_url_1: string;
  scenario_image_url: any | undefined;
  width: number;
  height: number;
  url: string;
  categoryId?: number;
  label?: string;
  discount_pct?: string | number;
}

/** Une case telle que reçue du backend : pas encore d'identité de placement. */
export type TemplateSlotSchema = Omit<ProductDisposition, 'instance_id'>;

export interface DashboardProduct {
  /** Voir ProductDisposition.instance_id — doit être miroité ici, sinon effacé à l'aller-retour orderChanged. */
  instance_id: string;
  product_id: string | null;
  image_url_1: string;
  scenario_image_url: any | undefined;
  ordre_generation: number;
  width: number;
  height: number;
  url: string;
  x?: number;
  y?: number;
  cols?: number;
  rows?: number;
  categoryId?: number;
  label?: string;
  discount_pct?: string | number;
}

@Component({
  selector: 'app-drag-drop-products',
  templateUrl: './drag-drop-products.component.html',
  styleUrls: ['./drag-drop-products.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DragDropProductsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() show: boolean = false;
  @Input() templateId: number | null = null;
  @Input() productsDisposition: ProductDisposition[] = [];
  @Input() selectedProducts: {
    instance_id: string;
    product_id: string | null;
    image_url_1: string;
    scenario_image_url: string | undefined;
    ordre_generation: number;
    width: number;
    height: number;
    url: string;
    label?: string;
    discount_pct?: string | number;
  }[] = [];
  @Output() orderChanged = new EventEmitter<{ updatedProducts: ProductDisposition[] }>();
  @Output() imageSelected = new EventEmitter<{ imageUrl: string, taskId: string }>();
  /**
   * Placements retirés par le parent. Les instance_id sont passés explicitement :
   * ngOnChanges exécute updateAfterSwap() AVANT ce bloc, donc les placements
   * supprimés ont déjà disparu du dashboard — une recherche ne trouverait rien.
   */
  @Input() resetIndex: { categoryId: Category['id'], productId: string | null, instanceIds: string[] } | null = null;
  @Input() applyDiscount: boolean = true;
  @Output() applyDiscountChange = new EventEmitter<boolean>();
  /** Lien unique : si actif et renseigné, tous les visuels produits du bloc redirigent vers globalLinkUrl. */
  @Input() applyGlobalLink: boolean = false;
  @Output() applyGlobalLinkChange = new EventEmitter<boolean>();
  @Input() globalLinkUrl: string = '';
  @Output() globalLinkUrlChange = new EventEmitter<string>();

  @ViewChild('dialogUploadImage') dialogUploadImage!: TemplateRef<any>;
  // Référence au conteneur Swapy
  @ViewChild('swapyContainer') swapyContainer!: ElementRef;

  selectedFile: File | null = null;
  errorMessage: string | null = null;
  panelOuvert: boolean = false;
  sidePanelHeight: string = '89%';
  sidePanelMarginTop: string = '90px';
  clickedProduct: DashboardProduct | null = null;
  private _showCreateGif: boolean = false;
  /** Index de carrousel courant, indexé par instance_id : chaque exemplaire défile indépendamment. */
  currentImageIndexes: { [key: string]: number } = {};
  /** Pool d'images, indexé par product_id : légitimement commun à tous les exemplaires d'un produit. */
  images: { [key: string]: (string | string[])[] } = {};
  private destroy$ = new Subject<void>();
  private scrappedImagesSubscription!: Subscription;
  sharedProducts: Product[] = [];
  loadingImages: { [key: string]: boolean } = {};
  blobCache: Map<string, SafeUrl> = new Map();
  // Cache to store preloaded images and prevent garbage collection
  private imageCache: HTMLImageElement[] = [];

  // Tableau servant de "dashboard" pour les produits
  dashboard: DashboardProduct[] = [];

  // Instance de Swapy
  swapyInstance: any;

  constructor(
    private generatorService: GeneratorService,
    private sharedDashboardProductService: SharedDashboardProductService,
    private scrappedImagesService: ScrappedImagesService,
    private sharedProductService: SharedProductService,
    private dialogService: NbDialogService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {

    this.selectedProducts.forEach((product) => {
      this.currentImageIndexes[product.instance_id] = 0;
      // Les cases vides n'ont pas de produit : ne pas créer une clé "null" dans les pools.
      if (product.product_id) {
        this.images[product.product_id] = [];
        this.loadingImages[product.product_id] = true;
      }
    });


    // Defer scraping images to prioritize main product images
    setTimeout(() => {
      this.scrappedImagesSubscription = this.scrappedImagesService.currentProducts.subscribe((productMap) => {
        productMap.forEach((urls, productId) => {
          if (!this.images[productId!]) {
            this.images[productId!] = [];
          }

          let hasValidImages = false;
          urls.forEach((url: string) => {
            if (!this.images[productId!].includes(url) && url !== "no image found") {
              this.images[productId!].push(url);
              hasValidImages = true;

            }
          });

          this.loadingImages[productId!] = false;
          this.resyncImageIndexes(productId!);

        });
        this.cdr.detectChanges();
      });
    }, 2000);

    this.sharedProductService.currentProducts.pipe(takeUntil(this.destroy$)).subscribe(products => {
      this.sharedProducts = products;

      this.sharedProducts.forEach(product => {
        if (!this.images[product.product_id]) {
          this.loadingImages[product.product_id!] = true;
          this.images[product.product_id] = [];
        }
        if (!this.images[product.product_id].includes(product.image_url_1)) {
          this.images[product.product_id].unshift(product.image_url_1);
          // Le unshift décale le pool partagé : réaligner les exemplaires concernés.
          this.resyncImageIndexes(product.product_id);
        }
        // Removed preloadNeighbors to prevent network flooding.
        // Preloading is now triggered by onload or mouseenter.
      });
    });
  }

  private initSwapy(): void {


    if (this.swapyContainer?.nativeElement) {
      //console.log("🚀 Initialisation de Swapy...");

      this.swapyInstance = createSwapy(this.swapyContainer.nativeElement, {
        animation: 'none',
        swapMode: 'drop',
        autoScrollOnDrag: true,
      });


      this.swapyInstance.onSwap((event: any) => {
        //console.log("↔️ Swap détecté:", event);
        this.handleSwapyItemChange(event);
        // ...
      });

      //console.log("slotItemMap()", this.swapyInstance.slotItemMap());
    }
  }


  private updateAfterSwap(): void {
    //console.log("🔄 Mise à jour après swap");

    // Mise à jour du dashboard
    this.updateDashboard();

    // Vérifier si Swapy est initialisé avant de le ré-initialiser
    if (this.swapyInstance) {
      //console.log("🔄 Mise à jour de Swapy...");
      //console.log(this.swapyContainer.nativeElement)
      setTimeout(() => {
        this.swapyInstance.update();
        //console.log("✅ Swapy mis à jour");
        //console.log("itemmap à jour :", this.swapyInstance.slotItemMap());
      }, 200);
    } else {
      //console.log("🚀 Initialisation de Swapy dans update...");
      setTimeout(() => this.initSwapy(), 0);
    }
  }


  handleSwapyItemChange(event: any): void {
    //console.log("🔄 Swapy event:", event);

    const fromSlot = Number(event.fromSlot);
    const toSlot = Number(event.toSlot);

    const fromIndex = this.dashboard.findIndex(d => d.ordre_generation === fromSlot);
    const toIndex = this.dashboard.findIndex(d => d.ordre_generation === toSlot);

    if (fromIndex !== -1 && toIndex !== -1) {

      // Swap properties (coordinates, order, etc.)
      [this.dashboard[fromIndex].ordre_generation, this.dashboard[toIndex].ordre_generation] =
        [this.dashboard[toIndex].ordre_generation, this.dashboard[fromIndex].ordre_generation];

      [this.dashboard[fromIndex].x, this.dashboard[toIndex].x] =
        [this.dashboard[toIndex].x, this.dashboard[fromIndex].x];

      [this.dashboard[fromIndex].y, this.dashboard[toIndex].y] =
        [this.dashboard[toIndex].y, this.dashboard[fromIndex].y];

      [this.dashboard[fromIndex].cols, this.dashboard[toIndex].cols] =
        [this.dashboard[toIndex].cols, this.dashboard[fromIndex].cols];

      [this.dashboard[fromIndex].rows, this.dashboard[toIndex].rows] =
        [this.dashboard[toIndex].rows, this.dashboard[fromIndex].rows];


      //console.log(" Dashboard mis à jour après swap:", this.dashboard);


      this.productsDisposition = this.dashboard.map(product => ({
        instance_id: product.instance_id,
        product_id: product.product_id,
        image_url_1: product.image_url_1,
        scenario_image_url: product.scenario_image_url,
        ordre_generation: product.ordre_generation,
        col: product.x ?? 0,
        row: product.y ?? 0,
        colspan: product.cols ?? 1,
        rowspan: product.rows ?? 1,
        width: product.width,
        height: product.height,
        url: product.url,
        categoryId: product.categoryId,
        label: product.label,
        discount_pct: product.discount_pct,
      }));

      //console.log(" productsDisposition mis à jour:", this.productsDisposition);

      // Émettre l'événement avec les nouvelles positions
      this.orderChanged.emit({
        updatedProducts: this.productsDisposition
      });


    } else {
      console.warn("⚠️ Impossible de trouver les éléments à échanger dans le dashboard");
    }
  }


  ngAfterViewInit(): void {

  }

  ngOnChanges(changes: SimpleChanges): void {
    //console.log("coucou change", this.show)
    if (changes['templateId'] || changes['selectedProducts']) {
      //console.log("this.productsDisposition", this.productsDisposition);
      //console.log("this.selectedProducts", this.selectedProducts);
      // Mise à jour du layout
      const maxCols = Math.max(...this.productsDisposition.map(p => p.col + p.colspan)) + 1;
      const maxRows = Math.max(...this.productsDisposition.map(p => p.row + p.rowspan)) + 1;
      const fixedColWidth = Math.min(200, (570 - maxCols * 10) / (maxCols - 1));
      const calculatedHeight = fixedColWidth * (maxRows - 1) + 10 * maxRows;
      this.sidePanelHeight = `${Math.min(calculatedHeight, window.innerHeight * 0.89)}px`;
      this.sidePanelMarginTop = `${Math.max((window.innerHeight - calculatedHeight) / 2 + 45, 90)}px`;


      this.updateAfterSwap();
    }
    if (this.show === false && this.swapyInstance) {
      this.swapyInstance.destroy()
      this.swapyInstance = null;
      this.dashboard = []
    }

    if (this.resetIndex) {
      // Purge l'état par placement des exemplaires retirés par le parent.
      this.resetIndex.instanceIds.forEach(id => {
        delete this.currentImageIndexes[id];
        delete this.disableTransition[id];
      });
      this.resetIndex = null;
    }
    setTimeout(() => {
      if (!this.swapyInstance) {
        //console.log("🚀 Réinitialisation de Swapy après mise à jour...");
        this.initSwapy();
      }
    }, 300);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.scrappedImagesSubscription) {
      this.scrappedImagesSubscription.unsubscribe();
    }
  }

  togglePanel() {
    this.panelOuvert = !this.panelOuvert;
  }

  // Même règle que le backend (generate_HTML.py), qui ignore toute autre URL.
  isGlobalLinkUrlValid(): boolean {
    return /^https?:\/\/\S+$/i.test((this.globalLinkUrl || '').trim());
  }

  getImageList(product: DashboardProduct): (string | string[])[] {
    const productImages: (string | string[])[] = [...(this.images[product.product_id!] || [])];
    if (
      product.scenario_image_url &&
      product.scenario_image_url !== '' &&
      product.scenario_image_url !== -1
    ) {
      // scenario_image_url peut etre un CSV de plusieurs URLs (depuis image_product_match)
      const scenarioUrls = typeof product.scenario_image_url === 'string'
        ? product.scenario_image_url.split(',').filter(u => u.trim() !== '')
        : [String(product.scenario_image_url)];
      // Ajouter en tete du carrousel (meilleure image en premier)
      for (let i = scenarioUrls.length - 1; i >= 0; i--) {
        const url = scenarioUrls[i].trim();
        if (!productImages.includes(url)) {
          productImages.unshift(url);
        }
      }
    }
    //attention limite à 10 images
    return productImages.slice(0, 10);
  }

  getProxyUrl(url: string | string[]): string {
    if (!url) return '';
    if (Array.isArray(url)) {
      return this.getProxyUrl(url[0]);
    }
    // Use the proxy to resize to width=150
    // Encode the URL to ensure special characters don't break the query params
    return `${environment.backendUrl}/api/proxy-image/?url=${encodeURIComponent(url)}&width=150`;
  }

  getCurrentImage(product: DashboardProduct): string | string[] {
    const productImages = this.getImageList(product);
    const currentIndex = this.currentImageIndexes[product.instance_id];
    const originalUrl = this.extractCurrentImage(productImages, currentIndex);

    if (Array.isArray(originalUrl)) {
      return originalUrl.map(url => this.getProxyUrl(url));
    }
    return this.getProxyUrl(originalUrl as string);
  }

  private extractCurrentImage(images: (string | string[])[], index: number): string | string[] {
    const imageElement = images[index] || images[0];
    return Array.isArray(imageElement) ? imageElement : imageElement || '';
  }

  // La clé contient DEUX choses, chacune indispensable :
  // - instance_id : garantit l'unicité, y compris pour deux exemplaires du même
  //   produit (une clé dupliquée lève NG0955).
  // - la position : Swapy déplace physiquement les nœuds DOM pendant le drag.
  //   C'est le changement de clé qui force Angular à détruire puis recréer les
  //   nœuds, seul moyen de resynchroniser le DOM avec le modèle après un swap.
  //   Avec une clé stable, Angular réutilise les nœuds laissés par Swapy et
  //   l'affichage ne suit plus le modèle.
  trackByItem(index: number, item: DashboardProduct): string {
    return `${item.instance_id}-${item.ordre_generation}-${item.x}-${item.y}-${item.cols}-${item.rows}`;
  }

  private updateProductDisposition(product: DashboardProduct, updatedImage: string | string[]): void {
    // Recherche par instance_id : sinon changer l'image de l'exemplaire n°2
    // l'écrirait dans la case de l'exemplaire n°1.
    const productIndex = this.productsDisposition.findIndex(p => p.instance_id === product.instance_id);
    if (productIndex !== -1) {
      // Si besoin, convertir en string (exemple : prendre le premier élément du tableau)
      const imageToAssign = Array.isArray(updatedImage) ? updatedImage[0] : updatedImage;
      this.productsDisposition[productIndex].image_url_1 = imageToAssign;

      // Sync with dashboard to prevent reversion during drag-and-drop
      const dashboardIndex = this.dashboard.findIndex(d => d.instance_id === product.instance_id);
      if (dashboardIndex !== -1) {
        this.dashboard[dashboardIndex].image_url_1 = imageToAssign;
      }
    }
    this.orderChanged.emit({
      updatedProducts: this.productsDisposition
    });

  }

  disableTransition: { [key: string]: boolean } = {};

  previousImage(event: MouseEvent, product: DashboardProduct): void {
    event.stopPropagation();
    const productImages = this.getImageList(product);
    const totalImages = productImages.length;
    const index = this.currentImageIndexes[product.instance_id] || 0;

    let nextIndex;
    if (index > 0) {
      nextIndex = index - 1;
      this.disableTransition[product.instance_id] = false;
    } else {
      nextIndex = totalImages - 1;
      // Looping: disable transition for instant jump
      this.disableTransition[product.instance_id] = true;
      setTimeout(() => {
        this.disableTransition[product.instance_id] = false;
        this.cdr.detectChanges();
      }, 50);
    }

    this.currentImageIndexes[product.instance_id] = nextIndex;
    const updatedImage = this.extractCurrentImage(productImages, this.currentImageIndexes[product.instance_id]);
    this.updateProductDisposition(product, updatedImage);
    this.preloadNeighbors(product);
  }

  nextImage(event: MouseEvent, product: DashboardProduct): void {
    event.stopPropagation();
    const productImages = this.getImageList(product);
    const index = this.currentImageIndexes[product.instance_id] || 0;

    let nextIndex;
    if (index < productImages.length - 1) {
      nextIndex = index + 1;
      this.disableTransition[product.instance_id] = false;
    } else {
      nextIndex = 0;
      // Looping: disable transition for instant jump
      this.disableTransition[product.instance_id] = true;
      setTimeout(() => {
        this.disableTransition[product.instance_id] = false;
        this.cdr.detectChanges();
      }, 50);
    }

    this.currentImageIndexes[product.instance_id] = nextIndex;
    const updatedImage = this.extractCurrentImage(productImages, this.currentImageIndexes[product.instance_id]);
    this.updateProductDisposition(product, updatedImage);
    this.preloadNeighbors(product);
  }

  selectImage(event: MouseEvent, product: DashboardProduct, index: number): void {
    event.stopPropagation();
    this.currentImageIndexes[product.instance_id] = index;
    const productImages = this.getImageList(product);
    const updatedImage = this.extractCurrentImage(productImages, this.currentImageIndexes[product.instance_id]);
    this.updateProductDisposition(product, updatedImage);
    this.preloadNeighbors(product);
  }

  adjustImageSizeOnLoad(event: Event, product: DashboardProduct): void {
    const imgElement = event.target as HTMLImageElement;
    const containerElement = imgElement.parentElement as HTMLElement;
    if (!imgElement || !containerElement) {
      return;
    }
    this.adjustImageSize(imgElement, containerElement);

    // Once the main image is loaded, preload its neighbors
    if (product?.product_id) {
      this.preloadNeighbors(product, 1);
    }
  }

  adjustImageSize(imgElement: HTMLImageElement, containerElement: HTMLElement): void {
    const imgWidth = imgElement.naturalWidth;
    const imgHeight = imgElement.naturalHeight;
    const containerWidth = containerElement.clientWidth;
    const containerHeight = containerElement.clientHeight;
    const imgRatio = imgWidth / imgHeight;
    const containerRatio = containerWidth / containerHeight;
    if (imgRatio > containerRatio) {
      imgElement.style.width = '100%';
      imgElement.style.height = 'auto';
    } else {
      imgElement.style.width = 'auto';
      imgElement.style.height = '100%';
    }
  }

  // Construit directement depuis productsDisposition, qui porte à la fois la
  // géométrie et le produit. L'ancienne version fusionnait selectedProducts[i]
  // avec le slot d'ordre_generation i+1, ce qui supposait que les deux tableaux
  // étaient dans le même ordre — hypothèse qui ne tenait que par accident.
  // selectedProducts reste l'@Input déclencheur de ngOnChanges (le parent le
  // réassigne alors qu'il mute productsDisposition en place).
  updateDashboard(): void {
    this.dashboard = this.productsDisposition.map(slot => ({
      instance_id: slot.instance_id,
      product_id: slot.product_id,
      image_url_1: slot.image_url_1,
      scenario_image_url: slot.scenario_image_url,
      ordre_generation: slot.ordre_generation,
      width: slot.width,
      height: slot.height,
      url: slot.url,
      x: slot.col ?? 0,          // ✅ Transformation col -> x
      y: slot.row ?? 0,          // ✅ Transformation row -> y
      cols: slot.colspan ?? 1,   // ✅ Transformation colspan -> cols
      rows: slot.rowspan ?? 1,   // ✅ Transformation rowspan -> rows
      categoryId: slot.categoryId,
      label: slot.label,
      discount_pct: slot.discount_pct
    }));

    //console.log("🛠 Mise à jour du dashboard avec les valeurs correctes :", this.dashboard);
  }


  removeProduct(product: DashboardProduct): void {
    // Recherche par instance_id : avec deux exemplaires du même produit, une
    // recherche par product_id viderait toujours le premier, pas celui cliqué.
    const index = this.dashboard.findIndex(p => p.instance_id === product.instance_id);
    if (index !== -1) {
      delete this.currentImageIndexes[this.dashboard[index].instance_id];
      delete this.disableTransition[this.dashboard[index].instance_id];
      this.dashboard[index].product_id = null;
      this.dashboard[index].image_url_1 = "";
      this.dashboard[index].scenario_image_url = undefined;
      this.dashboard[index].url = "";
      this.dashboard[index].label = '';
      this.dashboard[index].discount_pct = undefined;
      this.dashboard[index].categoryId = undefined;
      const updatedProducts = this.dashboard.map(p => ({
        instance_id: p.instance_id,
        product_id: p.product_id,
        image_url_1: p.image_url_1,
        scenario_image_url: p.scenario_image_url,
        ordre_generation: p.ordre_generation,
        col: p.x ?? 0,
        row: p.y ?? 0,
        colspan: p.cols ?? 1,
        rowspan: p.rows ?? 1,
        width: p.width,
        height: p.height,
        url: p.url,
        categoryId: p.categoryId,
        label: p.label,
        discount_pct: p.discount_pct,
      }));
      this.orderChanged.emit({
        updatedProducts: updatedProducts
      });
      this.productsDisposition = updatedProducts;
    }
    this.swapyInstance?.update()
  }

  onButtonClick(event: MouseEvent, product: DashboardProduct): void {
    event.stopPropagation();
    document.getElementById('file-upload-drag-and-drop')?.click();
    this.clickedProduct = product;
  }

  setClickedProduct(product: DashboardProduct): void {
    this.clickedProduct = product;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      if (this.clickedProduct) {
        this.uploadSelectedFile(this.clickedProduct);
      }
    }
  }

  uploadSelectedFile(product: DashboardProduct) {
    if (this.selectedFile) {
      const fileExtension = this.selectedFile.name.split('.').pop()?.toLowerCase();
      const allowedExtensions = ['jpeg', 'jpg', 'png', 'webp'];
      const fileSizeInMB = this.selectedFile.size / 1000000;

      if (!allowedExtensions.includes(fileExtension!)) {
        this.errorMessage = "Ce format de fichier n’est pas pris en charge. Vous pouvez importer un fichier .jpeg, .png ou .webp.";
        this.dialogService.open(this.dialogUploadImage);
        return;
      }

      if (fileSizeInMB > 5) {
        this.errorMessage = "La taille du fichier ne doit pas dépasser 5 Mo.";
        this.dialogService.open(this.dialogUploadImage);
        return;
      }

      this.generatorService.uploadFile(this.selectedFile).subscribe((uploadedImageUrl: string) => {
        const productImages = this.getImageList(product);
        productImages.unshift(uploadedImageUrl);
        this.images[product.product_id!] = productImages;
        this.currentImageIndexes[product.instance_id] = 0;
        const updatedImage = this.extractCurrentImage(productImages, this.currentImageIndexes[product.instance_id]);
        this.updateProductDisposition(product, updatedImage);
        // Le unshift ci-dessus a décalé le pool partagé : réaligner les autres exemplaires.
        this.resyncImageIndexes(product.product_id!, product.instance_id);
        this.preloadNeighbors(product);
      });
    } else {
      console.error("Aucun fichier sélectionné");
    }
  }

  handleCloseRequest() {
    this.showCreateGif = false;
  }

  get showCreateGif(): boolean {
    return this._showCreateGif;
  }

  set showCreateGif(value: boolean) {
    this._showCreateGif = value;
  }

  getFirstImageFromCarrousel(image: string | string[]): string {
    return Array.isArray(image) ? (image[0] || '') : (image || '');
  }

  createGif(event: MouseEvent, product: DashboardProduct) {
    this.showCreateGif = true;
    event.stopPropagation();
    this.clickedProduct = product;
    this.sharedDashboardProductService.addProduct(this.clickedProduct);
    this.cdr.detectChanges();
  }

  handleGifCreated(gifUrl: string) {
    if (this.clickedProduct) {
      const productImages = this.getImageList(this.clickedProduct);
      productImages.unshift(gifUrl);
      this.images[this.clickedProduct.product_id!] = productImages;
      this.currentImageIndexes[this.clickedProduct.instance_id] = 0;
      const updatedImage = this.extractCurrentImage(productImages, this.currentImageIndexes[this.clickedProduct.instance_id]);
      this.updateProductDisposition(this.clickedProduct, updatedImage);
      this.resyncImageIndexes(this.clickedProduct.product_id!, this.clickedProduct.instance_id);
      this.preloadNeighbors(this.clickedProduct);
    }
  }

  isGifImage(url: string | string[]): boolean {
    if (Array.isArray(url)) {
      return url[0].toLowerCase().endsWith('.gif');
    } else if (typeof url === 'string') {
      return url.toLowerCase().endsWith('.gif');
    }
    return false;
  }

  isCarrousel(image: string | string[]): boolean {
    return Array.isArray(image);
  }

  handleCarrouselCreated(carrouselImages: string[]) {
    if (this.clickedProduct) {
      const productImages = this.getImageList(this.clickedProduct);
      productImages.unshift(carrouselImages);
      this.images[this.clickedProduct.product_id!] = productImages;
      this.currentImageIndexes[this.clickedProduct.instance_id] = 0;
      const updatedImage = this.extractCurrentImage(productImages, this.currentImageIndexes[this.clickedProduct.instance_id]);
      this.updateProductDisposition(this.clickedProduct, updatedImage);
      this.resyncImageIndexes(this.clickedProduct.product_id!, this.clickedProduct.instance_id);
      this.preloadNeighbors(this.clickedProduct);
    }
  }

  /**
   * Le pool `images[product_id]` est partagé par tous les exemplaires d'un produit.
   * Un unshift (upload, GIF, carrousel, scraping) décale donc silencieusement le
   * carrousel des AUTRES exemplaires : leur index pointe vers une nouvelle image
   * alors que leur `image_url_1` — ce qui part réellement dans l'e-mail — n'a pas bougé.
   * On redérive l'index depuis l'URL réellement retenue par chaque exemplaire.
   * @param skipInstanceId exemplaire à l'origine du changement, déjà positionné.
   */
  private resyncImageIndexes(productId: string, skipInstanceId?: string): void {
    if (!productId) return;
    this.dashboard.forEach(d => {
      if (d.product_id !== productId || d.instance_id === skipInstanceId) return;
      const list = this.getImageList(d);
      const idx = list.findIndex(u => (Array.isArray(u) ? u[0] : u) === d.image_url_1);
      // idx === -1 : l'URL retenue n'est plus dans le pool (troncature à 10 images).
      // On laisse l'index en place plutôt que de forcer 0, qui afficherait une image
      // différente de celle réellement envoyée dans l'e-mail.
      if (idx >= 0) {
        this.currentImageIndexes[d.instance_id] = idx;
      }
    });
  }

  // Prend le placement entier : le pool d'images est par produit, mais l'index
  // courant est par exemplaire.
  preloadNeighbors(product: DashboardProduct, range: number = 1): void {
    if (!product?.product_id) return;
    const images = this.images[product.product_id];
    if (!images || images.length <= 1) return;

    const currentIndex = this.currentImageIndexes[product.instance_id] || 0;
    // Preload neighbors based on range
    for (let i = 1; i <= range; i++) {
      const nextIndex = (currentIndex + i) % images.length;
      const prevIndex = (currentIndex - i + images.length) % images.length;
      this.preloadSingleImage(images[nextIndex]);
      this.preloadSingleImage(images[prevIndex]);
    }
  }

  getImageSrc(url: string | string[]): SafeUrl | string {
    const proxyUrl = this.getProxyUrl(url);
    if (this.blobCache.has(proxyUrl)) {
      return this.blobCache.get(proxyUrl)!;
    }
    return proxyUrl;
  }

  preloadSingleImage(imageOrArray: string | string[]): void {
    const urls = Array.isArray(imageOrArray) ? imageOrArray : [imageOrArray];

    urls.forEach(url => {
      if (!url) return;

      const proxyUrl = this.getProxyUrl(url);

      if (this.blobCache.has(proxyUrl)) {
        return;
      }

      this.http.get(proxyUrl, { responseType: 'blob' }).subscribe(blob => {
        const objectUrl = URL.createObjectURL(blob);
        const safeUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
        this.blobCache.set(proxyUrl, safeUrl);
        this.cdr.detectChanges();
      }, err => {
        // Fallback or ignore
        // console.error('Failed to preload blob', err);
      });
    });
  }
  getSlotOrdre(item: DashboardProduct): number {
    const SlotOrdre = item.ordre_generation;
    return SlotOrdre;
  }

  // Identifiant d'item Swapy. DOIT être unique dans le conteneur (sinon Swapy lève
  // "your container has duplicate item ids") et stable entre deux cycles de détection
  // de changement (sinon slotItemMap est invalidé en permanence).
  // instance_id remplit les deux conditions, y compris pour deux exemplaires du même produit.
  getSlotName(item: DashboardProduct): string {
    return item.instance_id || `slot-${item.ordre_generation}`;
  }

  correctPosition(items: any[]): any[] {
    let occupiedCells = new Set<string>();

    return items.map(item => {
      let x = item.x ?? 0;
      let y = item.y ?? 0;
      const cols = item.cols ?? 1;
      const rows = item.rows ?? 1;

      // Vérifie si la cellule est déjà occupée
      while (this.isOverlapping(x, y, cols, rows, occupiedCells)) {
        y += 1; // Décale vers le bas
      }

      // Marque les cellules comme occupées
      for (let dx = 0; dx < cols; dx++) {
        for (let dy = 0; dy < rows; dy++) {
          occupiedCells.add(`${x + dx},${y + dy}`);
        }
      }

      return { ...item, x, y }; // Retourne l'élément avec la nouvelle position
    });
  }

  // Vérifie si une cellule est déjà occupée
  isOverlapping(x: number, y: number, cols: number, rows: number, occupiedCells: Set<string>): boolean {
    for (let dx = 0; dx < cols; dx++) {
      for (let dy = 0; dy < rows; dy++) {
        if (occupiedCells.has(`${x + dx},${y + dy}`)) {
          return true;
        }
      }
    }
    return false;
  }


}
